import { connect, useDispatch } from 'react-redux';
import { useField } from 'react-final-form';
import { Dispatch, useEffect } from 'react';
import cn from 'classnames';

import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';

import { SelectInputField } from '../../../UIPackage/MainModule/components/SelectInputField';
import { TextInputField } from '../../../UIPackage/MainModule/components/TextInputField';
import styles from './ErknmApproveRemark.module.scss';
import { ErknmApproveRemarkSectionCodeEnum } from '../enums/ErknmApproveRemarkSectionCodeEnum';
import { IKnmFormRef } from '../../../KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import {
  getKnmFormRef,
  getKnmIsErknmStatusApproving,
  getKnmIsErknmStatusCommentsExist,
  getKnmIsShowApproveRemarks,
} from '../../../KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmForm } from '../../../KnmPackage/MainModule/interfaces/IKnmForm';
import {
  getUserRoleIsProsecutor,
  getUserRoleIsSupervisor,
} from '../../../AuthPackage/MainModule/selectors/authSelectors';
import { RootActions } from '../../../CorePackage/MainModule/types/RootActions';
import { catalogGet } from '../../../CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from '../../../CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { IKnmRemarkSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IKnmRemarkSelectOption';
import { getCatalogKnmRemarkSelectOptions } from '../../../CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';
import { REQUIRED_ERROR } from '../../../CorePackage/MainModule/constants/validationErrors';
import { getApproveRemarkBlockId } from '../../MainModule/constants/knmErknmFieldsElementsIds';
import { approveRemarksToNote } from '../common/approveRemarksToNote';
import { approveRemarkToString } from '../common/approveRemarkToString';
import { KnmTypeCodesEnum } from '../../../APIPackage/CatalogsModule/enums/KnmTypeCodesEnum';
import {
  getIsPlanErknmStatusConsidered,
  getIsPlanErknmStatusUnderConsideration,
} from '../../../APIPackage/PlanListModule/common/getPlanErknmStatusFlags';
import { ApprovedTypesEnum } from '../../../APIPackage/KnmModule/enums/ApprovedTypesEnum';
import { ErknmPlanStatusesEnum } from '../../../APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import { getPlanListPlanErknmStatus } from '../../../PlanListPackage/MainModule/selectors/planListSelectors';

interface IErknmApproveRemarkProps {
  sectionCode: ErknmApproveRemarkSectionCodeEnum;
}

interface IErknmApproveRemarkStoreProps {
  formRef: IKnmFormRef | undefined;
  isProsecutor: boolean;
  isSupervisor: boolean;
  isKnmStatusApproving: boolean;
  isKnmStatusCommentsExist: boolean;
  knmPlanStatus: ErknmPlanStatusesEnum | undefined;
  isShowApproveRemarks: boolean | undefined;
  knmRemarkSelectOptions: IKnmRemarkSelectOption[];
}

const ErknmApproveRemarkWithoutStore = ({
  sectionCode,
  formRef,
  isProsecutor,
  isSupervisor,
  isKnmStatusApproving,
  isKnmStatusCommentsExist,
  knmPlanStatus,
  isShowApproveRemarks,
  knmRemarkSelectOptions,
}: IErknmApproveRemarkProps & IErknmApproveRemarkStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const approveRemarkFieldName = 'approveRemarks' as keyof IKnmForm;
  const typeErknm: IKnmForm['typeErknm'] = useField('typeErknm' as keyof IKnmForm, { subscription: { value: true } })
    .input.value;
  const approved: IKnmForm['approved'] = useField('approved' as keyof IKnmForm, { subscription: { value: true } }).input
    .value;
  const approveRemarks: IKnmForm['approveRemarks'] = useField(approveRemarkFieldName, {
    subscription: { value: true },
  }).input.value;

  const isPlanErknmStatusConsidered = getIsPlanErknmStatusConsidered(knmPlanStatus);
  const isOnApproval = approved?.value === ApprovedTypesEnum.ON_APPROVAL;
  const isApprovedWithRemark = approved?.value === ApprovedTypesEnum.APPROVED_WITH_REMARK;

  const isShowForm =
    isProsecutor &&
    typeErknm?.code === KnmTypeCodesEnum.PP &&
    getIsPlanErknmStatusUnderConsideration(knmPlanStatus) &&
    ((isKnmStatusApproving && (isOnApproval || isApprovedWithRemark)) ||
      (isKnmStatusCommentsExist && isApprovedWithRemark));

  const isShowInfoBlock =
    (isSupervisor && isKnmStatusCommentsExist && isPlanErknmStatusConsidered) || (isProsecutor && isShowApproveRemarks);

  const isApproveRemarkExist =
    Array.isArray(approveRemarks) && approveRemarks?.some(item => item.sectionCode === sectionCode);

  useEffect(() => {
    dispatch(catalogGet(CommonCatalogsNamesEnum.KNM_REMARKS));
  }, []);

  if (!isApproveRemarkExist) {
    return null;
  }

  const remove = formRef?.current.mutators.remove;
  const approveRemarkIndex = approveRemarks?.findIndex(item => item.sectionCode === sectionCode);
  const approveRemark = approveRemarks?.[approveRemarkIndex];
  const remarkFieldName = `${approveRemarkFieldName}[${approveRemarkIndex}].remark`;
  const descriptionFieldName = `${approveRemarkFieldName}[${approveRemarkIndex}].description`;
  const remarkSelectOptions = knmRemarkSelectOptions.filter(item => item.sectionCode === sectionCode);

  const remarkChangeHandle = () => {
    const approveNote = approveRemarksToNote(formRef?.current.getState().values.approveRemarks);

    formRef?.current.change('approveNote' as keyof IKnmForm, approveNote);
  };

  const removeHandle = () => {
    if (remove && isProsecutor) {
      remove(approveRemarkFieldName, approveRemarkIndex);
      remarkChangeHandle();
    }
  };

  if (isShowForm) {
    return (
      <div className={styles.RemarkBlock} id={getApproveRemarkBlockId(approveRemark?.listId)}>
        <span className={cn(styles.InfoIcon, styles.InfoIconMarginTop)}></span>
        <div className={styles.FieldsBlock}>
          <SelectInputField
            name={remarkFieldName}
            placeholder="Выберите замечание"
            className={styles.SelectInputField}
            options={remarkSelectOptions}
            validate={makeRequired(REQUIRED_ERROR)}
            onChange={remarkChangeHandle}
          />
          <TextInputField
            name={descriptionFieldName}
            placeholder="Введите подробности (при необходимости)"
            onBlur={remarkChangeHandle}
          />
        </div>
        <CloseButton className="shared-deleting-row-btn" onClick={removeHandle} />
      </div>
    );
  }

  if (isShowInfoBlock) {
    return (
      <div className={cn(styles.RemarkBlock, styles.RemarkBlockInfo)}>
        <span className={styles.InfoIcon}></span>
        <span>{approveRemarkToString(approveRemark)}</span>
      </div>
    );
  }

  return null;
};

const mapStateToProps = (state: IRootState): IErknmApproveRemarkStoreProps => ({
  formRef: getKnmFormRef(state),
  isProsecutor: getUserRoleIsProsecutor(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  isKnmStatusApproving: getKnmIsErknmStatusApproving(state),
  isKnmStatusCommentsExist: getKnmIsErknmStatusCommentsExist(state),
  knmRemarkSelectOptions: getCatalogKnmRemarkSelectOptions(state),
  knmPlanStatus: getPlanListPlanErknmStatus(state),
  isShowApproveRemarks: getKnmIsShowApproveRemarks(state),
});

export const ErknmApproveRemark = connect(mapStateToProps)(ErknmApproveRemarkWithoutStore);
